/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { shallowRef } from 'vue';
import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import requirement012 from '../../../../components/vue/individualWorkSupport/Requirement012.vue';
import { PHASE } from './constants';
import { execute } from './read';
import { writeData } from './write';
import { checkVariant } from './check';
import { saveData } from './write_variant';
import { exitLogic } from './exit';

export default {
  name: 'requirement037',
  data() {
    return {
      modalSize: 'md',
      phase: PHASE.START,
      isLoading: false,
      fixedPinNumber: '',
      pinNumber: '',
      validatePinNumber: undefined,
      selectedMode: '',
      currentWizard: '',
      systemId: '',
      // ダイアログフラグ
      modal_requirement_037: false,
      getVariantData: [],
      validateAppliedModel: undefined,
      validateOptionCode: undefined,
      chkValidateAppliedModel: false,
      chkValidateOptionCode: false,
      appliedModel: '',
      optionCode: '',
      twelveDigitModelCode: '',
      rowIndex: 0,
      resultApplied: '',
      resultOptionCode: '',
      displayMsg: '',
      lastEvaluatedKey: null
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  components: {
    'requirement012': requirement012
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.ERROR_PIN_NUMBER:
        case PHASE.INVALID_DATA:
          return 'danger';
        case PHASE.SUCCESS_12_MODEL:
          return 'success';
        case PHASE.START:
        case PHASE.EXECUTE:
        case PHASE.WARN:
        case PHASE.DISPLAY_PIN_NUMBER:
        case PHASE.INPUT_PIN_NUMBER:
        case PHASE.CHECKING:
        case PHASE.INPUT_DATA:
        case PHASE.DISPLAY_INPUT_DATA:
        case PHASE.WRITING:
        default:
          return 'secondary';
      }
    },

    /** モード選択一覧作成処理 */
    modeList() {
      return [
        { value: "saveData", text: this.$t(`${this.i18nWsKey}.label_mode_save_data`) },
        { value: "twelveDigitModel", text: this.$t(`${this.i18nWsKey}.label_mode_12_model`) }
      ];
    },

    /** システムテーブルのカラム */
    systemTableColumns() {
      return [
        { name: 'applied', label: this.displayMsg },
        { name: 'option_code', label: this.i18nWsKey + '.option_code' },
      ];
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.displayMsg = this.i18nWsKey + '.applied_model';
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_037 = true;
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_037 = false;
    },

    //----- htmlイベントメソッド ------------------------------------------------------//

    /** データ再登録警告でOKを押された時の処理 */
    displayPinNumber() {
      this.modalSize = 'md';
      this.phase = PHASE.DISPLAY_PIN_NUMBER;
    },

    /** 暗証番号表示でOKを押された時の処理 */
    inputPinNumber() {
      this.modalSize = 'lg';
      this.phase = PHASE.INPUT_PIN_NUMBER;
      this.pinNumber = this.fixedPinNumber;
      this.validatePinNumber = undefined;
    },

    /** 暗証番号入力でOKを押された時の処理 */
    validateInputPinNumber() {
      if (this.pinNumber === '') {
        this.fixedPinNumber = this.pinNumber;
        this.displayPinNumber();
        return;
      }

      this.validatePinNumber = undefined;
      if (this.pinNumber.length > 16) {
        this.validatePinNumber = { invalid: true, msg: this.$t(`message.alert_validate_max_length`, ['16']) };
        return;
      }
      if (!/^(\d+)$/i.test(this.pinNumber)) {
        this.validatePinNumber = { invalid: true, msg: this.$t(`message.alert_validate_invalid_pattern`) };
        return;
      }

      this.fixedPinNumber = this.pinNumber;
      this.displayPinNumber();
    },

    /** モードが選択された時の処理 */
    async selectMode() {
      switch (this.selectedMode) {
        case "saveData":
          // WS12機能を実行
          this.currentWizard = shallowRef(requirement012);
          this.modal_requirement_037 = false;
          break;
        case "twelveDigitModel":
          // バリアントデータ取得を実行
          await this.checkVariant();
          break;
        default:
          break;
      }
    },

    /** アプライドモデル・オプションコード入力画面へ遷移 */
    inputData() {
      this.phase = PHASE.INPUT_DATA;
      this.judgementTwelveDigitModel();
    },

    /** 12桁型式の判定 */
    judgementTwelveDigitModel() {
      this.appliedModel = '';
      this.optionCode = '';

      if (this.twelveDigitModelCode) {
        this.appliedModel = this.twelveDigitModelCode.slice(0, 7);
        this.optionCode = this.twelveDigitModelCode.slice(7).trim();
      }
    },

    /** バリデーションと一致判定 */
    validate() {
      try {
        this.isLoading = true;
        // バリデーション(入力チェック)の実行
        this.chkValidateAppliedModel = this.checkValidateAppliedModel();
        this.chkValidateOptionCode = this.checkValidateOptionCode();
        if (!this.chkValidateAppliedModel || !this.chkValidateOptionCode) {
          // アプライドモデル、オプションコードいずれかのバリデーションがエラーの場合処理終了
          return;
        }

        // アプライドモデル・オプションコード一致判定
        if (!this.chkInputParam(this.appliedModel, this.optionCode, this.getVariantData)) {
          // 無効なアプライドモデルとオプションコード
          // 非サポートエラー画面へ遷移
          this.phase = PHASE.INVALID_DATA;
          return;
        }

        // 有効なアプライドモデルとオプションコード
        // 車両情報表示画面へ遷移
        this.phase = PHASE.DISPLAY_INPUT_DATA;

      } finally {
        this.isLoading = false;
      }
    },

    /** 
     * アプライドモデルのバリデーションチェック
     * @returns バリデーション確認結果(true：バリデーションOK/false：バリデーションエラー)
     */
    checkValidateAppliedModel() {
      this.validateAppliedModel = undefined;

      // アプライドモデルが空の場合、エラーを出す
      if (!this.appliedModel) {
        this.validateAppliedModel = { valid: true, msg: this.$t(`message.alert_validate_required`) };
        return false;
      }

      // アプライドモデルの不正文字列の確認
      if (this.checkInvalidCharacter(this.appliedModel)) {
        this.validateAppliedModel = { valid: true, msg: this.$t(`message.alert_validate_invalid_pattern`) };
        return false;
      }

      // アプライドモデルの桁数チェック
      if (this.appliedModel.length !== 7) {
        this.validateAppliedModel = { valid: true, msg: this.$t(`message.alert_validate_max_length`, ['7']) };
        return false;
      }

      return true;
    },

    /** オプションコードのバリデーションチェック
     * @returns バリデーション確認結果(true：バリデーションOK/false：バリデーションエラー)
     */
    checkValidateOptionCode() {
      this.validateOptionCode = undefined;

      // オプションコードの不正文字列の確認
      if (this.checkInvalidCharacter(this.optionCode)) {
        this.validateOptionCode = { valid: true, msg: this.$t(`message.alert_validate_invalid_pattern`) };
        return false;
      }

      // オプションコードはアプライドモデルの入力値によってバリデーションの内容が変わるため
      // アプライドモデルのバリデーションがエラーの場合は後続のチェックを実行しない
      if (!this.chkValidateAppliedModel) {
        return true;
      }

      // アプライドモデルの5桁目の値の取得
      const fifthChar = this.appliedModel.slice(4, 5);
      const regExpNum = /^(\d+)$/i;

      // 5桁目が数字かどうかを判定
      if (regExpNum.test(fifthChar)) {
        // 数字の場合、オプションコードが3桁以下かのチェック
        if (this.optionCode.length > 3) {
          this.validateOptionCode = { valid: true, msg: this.$t(`message.alert_validate_max_length`, ['3']) };
          return false;
        }
      } else {
        // 英字の場合、オプションコードが4桁以下かのチェック
        if (this.optionCode.length > 4) {
          this.validateOptionCode = { valid: true, msg: this.$t(`message.alert_validate_max_length`, ['4']) };
          return false;
        }
      }

      return true;
    },

    /**
     * 使用不可な文字列が含まれていないかの確認関数
     * @param code 対象code
     */
    checkInvalidCharacter(code) {
      // 半角英数字以外の場合は弾く
      const halfReqExp = new RegExp(/^[A-Za-z0-9]*$/);
      return !halfReqExp.test(code);
    },

    /**
     * アプライドモデルとオプションコードの一致確認
     * @param inputAppliedModel アプライドモデルの入力値
     * @param inputOptionCode オプションコードの入力値
     * @param valiantData 取得したバリアントデータ群
     * @returns 一致確認結果(true：一致/false：不一致)
     */
    chkInputParam(inputAppliedModel, inputOptionCode, valiantData) {
      let idx = 0;
      let optionCode = "";
      let patternString = "";
      let regexPattern = "";
      let chkFlg = false;

      while (idx < valiantData.length) {
        optionCode = valiantData[idx].destination + valiantData[idx].op_code;
        // アプライドモデルの入力値が取得したバリアントデータのアプライドモデルと一致すること
        if (inputAppliedModel.toUpperCase() === valiantData[idx].applied.toUpperCase()) {
          // [_] を [""]に変換
          patternString = optionCode.replace(/_/g, "").toUpperCase();
          // [*] を [.?](任意の0文字または1文字)に変換
          regexPattern = new RegExp("^" + patternString.replace(/\*/g, ".?") + "$");
          // オプションコードの入力値が取得したバリアントデータの仕向値コード＋OPコードの連結文字列と一致すること
          if (regexPattern.test(inputOptionCode.toUpperCase())) {
            chkFlg = true;
            this.rowIndex = idx;
            break;
          }
        }
        idx++;
      }
      return chkFlg;
    },

    /** 車両情報表示画面でキャンセルボタン押下時の処理 */
    toSelectMode() {
      // モード選択画面へ遷移
      this.phase = PHASE.EXECUTE;
    },

    /** 車両情報表示画面でOKボタン押下時の処理 */
    async toWriting() {
      // 書き込み中画面へ遷移
      this.phase = PHASE.WRITING;
      await this.saveData();
    },

    //----- バックエンドAPI実行メソッド -----------------------------------------------//

    /** データ読み出し処理 */
    async execute() {
      await execute(this);
    },

    /** データ書き込み */
    async writeData() {
      await writeData(this);
    },

    /** バリアントデータ取得処理 */
    async checkVariant() {
      // 前回実行時のデータが残っているため初期化
      this.getVariantData = [];
      this.lastEvaluatedKey = null;
      await checkVariant(this);
    },

    /** 保存処理 */
    async saveData() {
      await saveData(this);
    },

    /**
     * 終了処理
     * @param {*} existence 0:エラー遷移あり, 1:エラー遷移なし
     * @returns
     */
    async exitLogic(existence) {
      await exitLogic(existence, this);
    },

    //----- 異常コールバックメソッド -----------------------------------------------//

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },

    /**
    * API 通信共通バリデーションエラー発生のコールバック
    * @param {object} result バリデーション結果
    */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },
  },

  /** ライフサイクルイベント */
  async mounted() {
    this.init();
    // データ読み出し
    await this.execute();
  },
};